import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* Chart — uPlot wrapper for React.
 *
 * Mirrors packages/design-system-svelte/Chart.svelte. Both implementations
 * MUST produce visually identical output on the same data + tone.
 *
 * Lifecycle rules (per ~/.claude/rules/saas-greenfield-pitfalls.md §4 and
 * ~/.claude/rules/uplot-charts.md):
 *   - Observers are created in useEffect(..., []) ONCE and disposed in the
 *     same effect's return — never recreated on prop changes
 *   - safeArrays() guards NaN, length mismatch, empty data
 *   - Real Unix-second x-values (not indices) so uPlot lays out correctly
 *   - ResizeObserver redraws on container width change
 *   - MutationObserver on <html> attributes redraws on theme switch
 *   - Empty state renders explicit "No data" UI — never a blank rect */
import { useEffect, useRef } from 'react';
import uPlot from 'uplot';
import { readToneColor } from '@sp/design-tokens';
function safeArrays(x, y) {
    if (!x || !y)
        return false;
    if (x.length === 0 || y.length === 0)
        return false;
    if (x.length !== y.length)
        return false;
    for (let i = 0; i < y.length; i++) {
        if (!Number.isFinite(y[i]) || !Number.isFinite(x[i]))
            return false;
    }
    return true;
}
function readTokens() {
    if (typeof document === 'undefined') {
        // SSR — caller must not render Chart on the server
        return {
            ink: '', inkSec: '', inkTer: '', line: '',
            accent: '', ok: '', watch: '', warn: '', alarm: '', bg: '',
        };
    }
    const r = getComputedStyle(document.documentElement);
    return {
        ink: r.getPropertyValue('--ink-primary').trim(),
        inkSec: r.getPropertyValue('--ink-secondary').trim(),
        inkTer: r.getPropertyValue('--ink-tertiary').trim(),
        line: r.getPropertyValue('--border-hairline').trim(),
        accent: r.getPropertyValue('--accent').trim(),
        ok: r.getPropertyValue('--signal-ok').trim(),
        watch: r.getPropertyValue('--signal-watch').trim(),
        warn: r.getPropertyValue('--signal-warn').trim(),
        alarm: r.getPropertyValue('--signal-alarm').trim(),
        bg: r.getPropertyValue('--bg-surface').trim(),
    };
}
export function Chart({ x, y, height = 200, tone = 'ink', thresholds, units = '', className, style, }) {
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    // Track props in refs so the observer effect can read current values
    // without resubscribing every render.
    const propsRef = useRef({ x, y, height, tone, thresholds, units });
    propsRef.current = { x, y, height, tone, thresholds, units };
    function build() {
        const container = containerRef.current;
        if (!container)
            return;
        const p = propsRef.current;
        chartRef.current?.destroy();
        chartRef.current = null;
        if (!safeArrays(p.x, p.y))
            return;
        const tokens = readTokens();
        const stroke = readToneColor(p.tone) || tokens.ink;
        const opts = {
            width: container.clientWidth,
            height: p.height,
            cursor: { drag: { x: true, y: false }, focus: { prox: 30 } },
            legend: { show: false },
            scales: { x: { time: true }, y: { auto: true } },
            axes: [
                {
                    stroke: tokens.inkTer,
                    grid: { stroke: tokens.line, dash: [2, 2], width: 1 },
                    ticks: { show: false },
                    font: '11px "Geist Mono", "JetBrains Mono", monospace',
                    size: 28,
                },
                {
                    stroke: tokens.inkTer,
                    grid: { stroke: tokens.line, dash: [2, 2], width: 1 },
                    ticks: { show: false },
                    font: '11px "Geist Mono", "JetBrains Mono", monospace',
                    size: 44,
                    values: (_u, splits) => splits.map((v) => `${v.toFixed(1)}${p.units}`),
                },
            ],
            series: [
                { value: (_u, v) => (v ? new Date(v * 1000).toLocaleString() : '') },
                { stroke, width: 1.5, points: { show: false } },
            ],
            hooks: {
                draw: [
                    (u) => {
                        const th = p.thresholds;
                        if (!th)
                            return;
                        const drawLine = (val, col) => {
                            const yPx = u.valToPos(val, 'y', true);
                            if (!Number.isFinite(yPx))
                                return;
                            const ctx = u.ctx;
                            ctx.save();
                            ctx.strokeStyle = col;
                            ctx.setLineDash([4, 3]);
                            ctx.lineWidth = 1;
                            ctx.beginPath();
                            ctx.moveTo(u.bbox.left, yPx);
                            ctx.lineTo(u.bbox.left + u.bbox.width, yPx);
                            ctx.stroke();
                            ctx.restore();
                        };
                        if (th.warn !== undefined)
                            drawLine(th.warn, tokens.warn);
                        if (th.alarm !== undefined)
                            drawLine(th.alarm, tokens.alarm);
                    },
                ],
            },
        };
        chartRef.current = new uPlot(opts, [p.x, p.y], container);
    }
    // ONE-TIME observer setup. Empty deps array — DO NOT add deps that would
    // recreate observers on every prop change (would leak refs in dev HMR).
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return undefined;
        const ro = new ResizeObserver(() => {
            const c = chartRef.current;
            const el = containerRef.current;
            if (c && el)
                c.setSize({ width: el.clientWidth, height: propsRef.current.height });
        });
        ro.observe(container);
        const mo = new MutationObserver(() => build());
        mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => {
            ro.disconnect();
            mo.disconnect();
            chartRef.current?.destroy();
            chartRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Rebuild on prop changes — does NOT recreate observers.
    useEffect(() => {
        build();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [x, y, height, tone, thresholds, units]);
    const wrapperStyle = {
        position: 'relative',
        width: '100%',
        minHeight: height,
        ...style,
    };
    return (_jsxs("div", { className: className, style: wrapperStyle, children: [_jsx("div", { ref: containerRef, style: { width: '100%', height } }), !safeArrays(x, y) && (_jsx("div", { style: {
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: '12px',
                    color: 'var(--ink-tertiary)',
                    pointerEvents: 'none',
                }, children: "No data" }))] }));
}
//# sourceMappingURL=Chart.js.map